import {
  ClerkProvider,
  Show,
  useAuth,
} from "@clerk/react";
import { frFR } from "@clerk/localizations";
import { dark } from "@clerk/themes";
import { Loader2 } from "lucide-react";
import { PostHogConfig } from "posthog-js";
import { PostHogErrorBoundary, PostHogProvider } from "posthog-js/react";
import { useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { App } from "./App.tsx";
import { appSurfaceProperties } from "@/lib/analytics-surface";
import { registerPreloadErrorReload } from "@/lib/preload-error-reload";
import { queryClient } from "./lib/query-client";
import "./i18n/config";
import "./index.css";
import SignInPage from "./pages/auth/sign-in.tsx";
import SignUpPage from "./pages/auth/sign-up.tsx";

registerPreloadErrorReload();

// Log app version for debugging cache issues
console.log(
  `%c🚀 Shaarp UI`,
  `\nMode: ${import.meta.env.MODE}`,
  `\nLoaded at: ${new Date().toLocaleString("fr-FR")}`,
  `\nDev: ${import.meta.env.DEV}`
);

const posthogOptions: Partial<PostHogConfig> = {
  api_host: "https://r.shaarp.ai",
  person_profiles: "identified_only",
  // A pageview on every change of path, not only on a full load: the app is a
  // single page, and on a phone a session is often one load. Same-URL history
  // entries (an overlay closed by the back gesture) and query strings (`?m=`)
  // change no path and send nothing.
  capture_pageview: "history_change",
  // Before the first pageview, which `loaded` precedes: every event says which
  // tree and which display mode it came from (`useAppSurfaceAnalytics` keeps
  // it in step after a resize).
  loaded: (posthog) => {
    posthog.register(appSurfaceProperties());
  },
  capture_exceptions: true,
  before_send: (event) => {
    if (!event) return event;
    if (event.event === "$exception") {
      const exceptionList =
        (event.properties?.["$exception_list"] as
          | Array<Record<string, string>>
          | undefined) ?? [];
      const exception = exceptionList[0];
      if (exception) {
        const type = exception["$exception_type"] ?? "";
        const message = exception["$exception_message"] ?? "";
        // Transient Clerk SDK network errors (session keep-alive failures)
        if (message.includes("ClerkJS: Network error")) return null;
        // Browser throttle on history.replaceState (triggered by Clerk error recovery)
        if (type === "DOMException" && message.includes("history.replaceState()"))
          return null;
        // Third-party browser extensions mutate the DOM; React's removeChild then
        // fails on a node it no longer owns. Not actionable from our code.
        if (
          type === "DOMException" &&
          message.includes("removeChild") &&
          message.includes("not a child of this node")
        )
          return null;
        // PostHog SDK itself throws when JSON.stringify hits the V8 string
        // length limit (~512MB) on a large payload. Bug in the SDK, not us.
        if (type === "RangeError" && message === "Invalid string length")
          return null;
      }
    }
    return event;
  },
  session_recording: {
    maskAllInputs: false,
    maskInputOptions: {
      password: true, // Highly recommended as a minimum!!
      color: false,
      date: false,
      "datetime-local": false,
      email: true,
      month: false,
      number: false,
      range: false,
      search: false,
      tel: true,
      text: false,
      time: false,
      url: false,
      week: false,
      textarea: false,
      select: false,
    },
  },
};

/** Detect if the user prefers dark mode (system or localStorage). */
function useIsDarkMode(): boolean {
  const [isDark, setIsDark] = useState(() => {
    const stored = localStorage.getItem("ui-theme");
    if (stored === "dark") return true;
    if (stored === "light") return false;
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => {
      const stored = localStorage.getItem("ui-theme");
      if (!stored || stored === "system") {
        setIsDark(e.matches);
      }
    };
    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, []);

  return isDark;
}

/** Show a loading spinner while Clerk restores the session. */
function ClerkLoadingGuard({ children }: { children: React.ReactNode }) {
  const { isLoaded } = useAuth();

  if (!isLoaded) {
    return (
      <div className="flex min-h-svh items-center justify-center">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return <>{children}</>;
}

/** Redirect signed-in users away from auth pages (e.g. after magic link verification). */
function AuthRedirectGuard({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();

  if (pathname.startsWith("/sign-in") || pathname.startsWith("/sign-up")) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

/** Below 16px, iOS zooms the page into the field and never zooms back out. */
const CLERK_INPUT_FONT_SIZE = { fontSize: "16px" } as const;

function ClerkProviderWithRoutes() {
  const navigate = useNavigate();
  const isDarkMode = useIsDarkMode();

  return (
    <ClerkProvider
      publishableKey={import.meta.env.VITE_CLERK_PUBLISHABLE_KEY}
      localization={frFR}
      routerPush={(to) => navigate(to)}
      routerReplace={(to) => navigate(to, { replace: true })}
      signInUrl="/sign-in"
      signUpUrl="/sign-up"
      signInFallbackRedirectUrl="/"
      signUpFallbackRedirectUrl="/"
      appearance={{
        theme: isDarkMode ? dark : undefined,
        variables: {
          colorPrimary: isDarkMode ? "#6548fa" : "hsl(219.1, 83.1%, 15.7%)",
        },
        elements: {
          formFieldInput: CLERK_INPUT_FONT_SIZE,
          otpCodeFieldInput: CLERK_INPUT_FONT_SIZE,
        },
      }}
    >
      <PostHogProvider
        apiKey={import.meta.env.VITE_POSTHOG_API_KEY}
        options={posthogOptions}
      >
        <PostHogErrorBoundary>
          <QueryClientProvider client={queryClient}>
            <ClerkLoadingGuard>
              <Show when="signed-in">
                <AuthRedirectGuard>
                  <App />
                </AuthRedirectGuard>
              </Show>
              <Show when="signed-out">
                <Routes>
                  <Route path="/sign-in/*" element={<SignInPage />} />
                  <Route path="/sign-up/*" element={<SignUpPage />} />
                  <Route
                    path="*"
                    element={<Navigate to="/sign-in" replace />}
                  />
                </Routes>
              </Show>
            </ClerkLoadingGuard>
          </QueryClientProvider>
        </PostHogErrorBoundary>
      </PostHogProvider>
    </ClerkProvider>
  );
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <BrowserRouter>
    <ClerkProviderWithRoutes />
  </BrowserRouter>
);
